'use client'

import {Box, Container, SimpleGrid, Stack, Text} from '@blocks'
import {
   sitePrimaryHex,
   siteSectionBodyHex,
   siteSectionHeadingHex,
   siteTypographyEyebrowFontSizeDesktop,
   siteTypographyEyebrowFontSizeMobile,
   siteTypographySectionLeadFontSizeDesktop,
   siteTypographySectionLeadFontSizeMobile,
   siteTypographySectionTitlePrimaryFontSizeDesktop,
   siteTypographySectionTitlePrimaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {
   IconCalendar,
   IconHeart,
   IconMapPin,
   IconSparkles
} from '@icons'

const milestones = [
   {
      icon: IconCalendar,
      year: '1997',
      title: 'Est. as Yello K Wash',
      description:
         'Clean Park has been part of the Morrinsville community since 1997, originally operating as Yello K Wash.'
   },
   {
      icon: IconSparkles,
      year: '2021',
      title: 'Rebranded to Clean Park',
      description:
         'In 2021 the site was renamed to Clean Park, refreshing the brand while continuing its long-standing community presence.'
   },
   {
      icon: IconMapPin,
      year: 'Local',
      title: 'Proudly Local',
      description:
         "Owned and operated by Morrinsville locals, we've always been about providing a practical, reliable place to keep your vehicle clean."
   },
   {
      icon: IconHeart,
      year: 'Today',
      title: 'Here for the Community',
      description:
         'We take pride in keeping the site tidy, well-maintained, and ready for the community that has supported us over the years.'
   }
]

export const About = () => {
   const isMobile = useMediaQuery('(max-width: 768px)')

   return (
      <Stack gap={0}>
         <Box bg={'white'}>
            <Container
               py={isMobile ? 70 : 110}
               size={'xl'}
            >
               <SimpleGrid
                  cols={{base: 1, md: 2}}
                  spacing={isMobile ? 40 : 80}
               >
                  <Stack gap={isMobile ? 20 : 28}>
                     <Text
                        c={'rgba(34,70,130,0.4)'}
                        fz={
                           isMobile
                              ? siteTypographyEyebrowFontSizeMobile
                              : siteTypographyEyebrowFontSizeDesktop
                        }
                        fw={700}
                        tt={'uppercase'}
                        lts={2}
                     >
                        Our Story
                     </Text>
                     <Text
                        c={siteSectionHeadingHex}
                        fz={
                           isMobile
                              ? siteTypographySectionTitlePrimaryFontSizeMobile
                              : siteTypographySectionTitlePrimaryFontSizeDesktop
                        }
                        fw={800}
                        lh={1.2}
                     >
                        A Friendly Local Touch
                     </Text>
                     <Stack gap={18}>
                        <Text
                           c={siteSectionBodyHex}
                           fz={17}
                           fw={500}
                           lh={1.8}
                        >
                           We're proudly owned and operated by
                           Morrinsville locals, and we've always been
                           about providing a practical, reliable place
                           for people to keep their vehicles clean.
                           Whether you're popping in for a quick wash,
                           using the self-serve bays, giving the dog a
                           scrub, or booking in for detailing, we aim
                           to make it easy and convenient.
                        </Text>
                        <Text
                           c={siteSectionBodyHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.8}
                        >
                           Being local means we care about doing
                           things properly. We take pride in keeping
                           the site tidy, well-maintained, and ready
                           for the community that's supported us over
                           the years.
                        </Text>
                        <Text
                           c={siteSectionBodyHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.8}
                        >
                           From everyday washes through to detailing,
                           Clean Park is here to offer straightforward
                           service, quality facilities, and a friendly
                           local touch.
                        </Text>
                     </Stack>
                  </Stack>

                  <SimpleGrid
                     cols={{base: 1, sm: 2}}
                     spacing={16}
                     style={{alignContent: 'start'}}
                  >
                     {milestones.map((milestone) => (
                        <Box
                           key={milestone.year}
                           p={isMobile ? 24 : 32}
                           style={{
                              background: '#f4f7ff',
                              borderRadius: 20,
                              border: '1px solid #dce5f5'
                           }}
                        >
                           <Stack gap={12}>
                              <Text
                                 c={'rgba(34,70,130,0.35)'}
                                 fz={32}
                                 fw={900}
                                 lh={1}
                              >
                                 {milestone.year}
                              </Text>
                              <milestone.icon
                                 size={26}
                                 color={sitePrimaryHex}
                                 stroke={1.6}
                              />
                              <Stack gap={6}>
                                 <Text
                                    c={siteSectionHeadingHex}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={700}
                                    lh={1.3}
                                 >
                                    {milestone.title}
                                 </Text>
                                 <Text
                                    c={siteSectionBodyHex}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={500}
                                    lh={1.65}
                                 >
                                    {milestone.description}
                                 </Text>
                              </Stack>
                           </Stack>
                        </Box>
                     ))}
                  </SimpleGrid>
               </SimpleGrid>
            </Container>
         </Box>
      </Stack>
   )
}
